import React, { useEffect } from "react"
import cx from "classnames"

import Badge from "@kit/design/badge"
import Button from "@kit/design/button"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"

import { FeatureList } from "./plan-feature-list"

import { usePlanComparisonContext } from "./context"
import { analytics } from "utils/analytics"

const BillingIntervalNote = ({ plan, hasAnyPromoApplied }) => {
  if (hasAnyPromoApplied) {
    return null
  }

  if (plan.monthly_difference) {
    return (
      <div className="text-gray-700 -mt-1" data-testid="billing-interval-note">
        New total: <span className="font-bold">{plan.text}</span> billed{" "}
        {plan.interval}ly
      </div>
    )
  }

  return (
    <div className="text-gray-700 -mt-1" data-testid="billing-interval-note">
      <span className="font-bold">{plan.text}</span> billed {plan.interval}ly
      {plan.interval === "year" && (
        <span className="italic"> (Save {plan.annual_savings} per year)</span>
      )}
    </div>
  )
}

const PlanCard = ({ plan, buttonText, multiplePlans = true }) => {
  const { handlePlanSelection, hasAnyPromoApplied, featuredPlan } =
    usePlanComparisonContext()

  const isLargeScreen = useMediaBreakpoint("lg")
  const isFeatured = featuredPlan?.id === plan.id
  const highlight = isFeatured && multiplePlans && isLargeScreen

  const hasPlanPromo = plan.promo_code?.valid
  const showMonthlyDifference = plan.monthly_difference && !hasAnyPromoApplied
  const showMonthlyEquivalent = plan.interval === "year" && !hasAnyPromoApplied

  const displayIntervalMap = {
    month: "mo",
    year: "yr"
  }

  const displayInterval =
    showMonthlyDifference || showMonthlyEquivalent
      ? displayIntervalMap["month"]
      : displayIntervalMap[plan.interval]

  let price
  if (hasPlanPromo) {
    price = plan.promo_code.text
  } else if (showMonthlyDifference) {
    price = plan.monthly_difference
  } else if (showMonthlyEquivalent) {
    price = plan.monthly_amount
  } else {
    price = plan.text
  }

  const defaultButtonText = `Upgrade to ${plan.display_name}`

  // Track that this plan card was shown, noting whether the Creator Pro
  // monthly price difference was displayed (vs. the full plan price) so we
  // can measure the impact of clarify_upgrade_pricing changes.
  useEffect(() => {
    analytics.track("Element", "Opened", {
      element_type: "plan_card",
      name: "plan_comparison_plan_card",
      location: "plan_comparison",
      service_level: plan.service_level,
      plan_id: plan.id,
      interval: plan.interval,
      monthly_difference_displayed: Boolean(showMonthlyDifference),
      monthly_difference: plan.monthly_difference ?? null
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleClick(plan) {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "plan_comparison_upgrade_button",
      text: buttonText || defaultButtonText,
      service_level: plan.service_level,
      location: "plan_comparison",
      monthly_difference_displayed: Boolean(showMonthlyDifference),
      monthly_difference: plan.monthly_difference ?? null
    })

    handlePlanSelection(plan)
  }

  return (
    <div
      data-testid={`${plan.service_level}-features`}
      className={cx(
        "bg-white lg:p-6 rounded-lg lg:border flex flex-col h-full",
        {
          ["border-green-400"]: highlight
        }
      )}
    >
      <div className="flex flex-col flex-grow">
        <div className="flex flex-row justify-between items-center h-auto mb-1">
          <span
            className="font-semibold text-xl text-black font-heading"
            data-testid="plan-header"
          >
            {plan.display_name}
          </span>
          {isFeatured && multiplePlans && (
            <Badge color="green" variant="light" size="lg">
              Recommended
            </Badge>
          )}
        </div>

        <p className="mb-3">{plan.description}</p>

        <div className="mb-5">
          <div className="text-black" data-testid="price-display">
            {hasPlanPromo && (
              <span className="line-through text-2xl mr-3 text-gray-900 font-heading">
                {plan.text}
              </span>
            )}
            <span className="text-4xl font-heading">{price}</span>
            <span
              className="text-xl"
              aria-label={displayInterval === "mo" ? "per month" : "per year"}
            >
              /{displayInterval}
            </span>
          </div>

          <BillingIntervalNote
            plan={plan}
            hasAnyPromoApplied={hasAnyPromoApplied}
          />
        </div>

        <FeatureList plan={plan} />
      </div>

      <Button
        data-testid={`upgrade-button-${plan.id}`}
        className="w-full"
        size="lg"
        is={isFeatured ? "primary" : "secondary"}
        onClick={() => handleClick(plan)}
      >
        {buttonText || defaultButtonText}
      </Button>
    </div>
  )
}

export { PlanCard }
